import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";
import "../css/returns.scss";

const returnSteps = [
  {
    title: "Start a request",
    copy: "Send us the item name, the reason for the return, and the order number from your confirmation.",
  },
  {
    title: "Review the instructions",
    copy: "We will confirm eligibility and provide the return address and packing guidance before anything is sent.",
  },
  {
    title: "Pack it carefully",
    copy: "Return the complete item, including its original packaging, refills, inserts, and any bundled accessories.",
  },
];

function Returns() {
  return (
    <main className="editorial-page policy-page returns-page">
      <div className="editorial-page__inner">
        <header className="editorial-page__hero">
          <p className="editorial-page__eyebrow">Customer care</p>
          <h1>Returns &amp; exchanges</h1>
          <p className="editorial-page__lede">
            You have 30 calendar days from recorded delivery to request a return
            for an eligible item.
          </p>
          <p className="policy-updated">
            Last updated <time dateTime="2026-10-19">October 19, 2026</time>
          </p>
        </header>

        <nav className="policy-nav" aria-label="Return policy topics">
          <a href="#eligibility">Eligibility</a>
          <a href="#return-process">How it works</a>
          <a href="#refunds">Refunds</a>
          <a href="#problems">Damaged items</a>
          <a href="#cancellations">Cancellations</a>
        </nav>

        <section className="policy-section" id="eligibility">
          <p className="editorial-page__section-label">The essentials</p>
          <h2>What can be returned</h2>
          <p>
            Eligible items may be returned within 30 days of the recorded delivery
            date. Contact us within that period. Items must be unused, clean, and
            in saleable condition with all original packaging, labels, bundled
            parts, and proof of purchase.
          </p>
          <div className="return-eligibility">
            <div>
              <h3>Eligible</h3>
              <ul className="policy-list">
                <li>Unused pens, pencils, cases, and desk tools</li>
                <li>Unmarked notebooks and unopened paper goods</li>
                <li>Unopened refills in their original sealed packaging</li>
                <li>Full-price items with bundled parts intact</li>
              </ul>
            </div>
            <div>
              <h3>Not eligible</h3>
              <ul className="policy-list">
                <li>Personalized or monogrammed goods</li>
                <li>Opened ink, paint, markers, adhesives, or refills</li>
                <li>Gift cards and items marked final sale</li>
                <li>Goods damaged through misuse or normal wear</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="policy-section" id="return-process">
          <p className="editorial-page__section-label">A simple process</p>
          <h2>How a return works</h2>
          <ol className="return-steps">
            {returnSteps.map((step, index) => (
              <li key={step.title}>
                <span aria-hidden="true">0{index + 1}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.copy}</p>
                </div>
              </li>
            ))}
          </ol>
          <p>
            Original delivery charges are not normally refundable. Return postage
            is the customer&apos;s responsibility unless the item arrived incorrect,
            damaged, defective, or applicable law requires otherwise. Do not send
            a parcel until we provide return instructions; unauthorized returns may
            be delayed or sent back.
          </p>
          <p>
            You are responsible for suitable packaging. We recommend a tracked
            service because a customer-arranged return remains the customer&apos;s
            responsibility until received, except where applicable law provides
            otherwise.
          </p>
        </section>

        <section className="policy-section" id="refunds">
          <p className="editorial-page__section-label">After inspection</p>
          <h2>Refunds and exchanges</h2>
          <p>
            We inspect returns and email the outcome. Approved refunds are sent to
            the original payment method. We normally initiate an approved refund
            within five business days after inspection; the card issuer or payment
            provider may need additional time to post the credit.
          </p>
          <p>
            For an exchange, the clearest route is to return the original item and
            place a new order for the preferred color, nib, ruling, or size. This
            avoids holding an item while the return is in transit.
          </p>
          <p>
            If a returned item is used, incomplete, damaged after delivery, or
            otherwise ineligible, we may decline the voluntary return or make a
            lawful deduction reflecting the loss in value after explaining the
            reason. This does not affect a remedy required for defective, unsafe,
            or misdescribed goods.
          </p>
        </section>

        <section className="policy-section" id="problems">
          <p className="editorial-page__section-label">Something went wrong</p>
          <h2>Damaged, incorrect, or missing items</h2>
          <p>
            Please report a problem within seven days of delivery and keep all
            packaging. Include a clear description and photographs of the item
            and parcel so the team can assess it quickly.
          </p>
          <p>
            Do not dispose of a damaged product until you receive instructions,
            particularly if it contains ink, paint, or another liquid.
          </p>
          <p>
            Nothing in this policy excludes a right or remedy that cannot lawfully
            be excluded. A voluntary 30-day return is separate from any rights you
            may have when goods are defective, unsafe, or materially different
            from their description.
          </p>
        </section>

        <section className="policy-section" id="cancellations">
          <p className="editorial-page__section-label">Before dispatch</p>
          <h2>Cancellations and refused parcels</h2>
          <p>
            Email <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>{" "}
            promptly to request cancellation. We cannot guarantee cancellation
            after packing or dispatch. Refusing or failing to collect a parcel is
            not an approved return; carrier return fees or non-refundable import
            charges may be deducted where permitted by law.
          </p>
        </section>

        <aside className="policy-demo-note" aria-label="Return help">
          <h2>Need help with a return?</h2>
          <p>
            Please <Link to="/contact">contact our customer-care team</Link> before
            sending an item, or email <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>,
            so we can provide the correct return instructions.
          </p>
        </aside>
        <p className="policy-back-link"><Link to="/policies">View all store policies</Link></p>
      </div>
    </main>
  );
}

export default Returns;
